import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import {
  BarChart,
  Business,
  Category,
  Description,
  Groups,
  Inventory,
  Language,
  Person,
  Repeat,
  Storefront,
  SwapHoriz
} from "@mui/icons-material";
import { Country } from "../../../../api/Api";
import { useUpdateCountry } from "../../dataHooks/countryQueries";
import { MFT_ENERGY_AS, mftCompanies } from "../../../../utils/consts";
import { CountrySelect } from "../../../../components/countrySelect";
import { UserNamePicker } from "../../../../components/userPicker";

type Props = {
  open: boolean;
  onClose: () => void;
  specificCountry: Country;
};

const UNIT_OPTIONS = ["DKK", "EUR", "GBP", "JPY", "TRY", "USD", "MW"];

type CountryFormState = {
  tradingTeam: string;
  contactPersonTradingTeam: string;
  countryName: string;
  tsoName: string;
  tsoContactName: string;
  tsoContactEmail: string;
  exchangeName: string;
  exchangeContactName: string;
  exchangeContactEmail: string;
  clearingBankName: string;
  clearingBankContactName: string;
  clearingBankContactEmail: string;
  tradingSettlementType: string;
  productType: string;
  venue: string;
  transactionType: string;
  dealVolume?: number;
  unit: string;
  mandate: boolean;
  urgent: boolean;
  contractingEntity: string;
  businessRational: string;
};

const buildFormState = (country: Country): CountryFormState => ({
  tradingTeam: country.tradingTeam || "",
  contactPersonTradingTeam: country.contactPersonTradingTeam || "",
  countryName: country.countryName || "",
  tsoName: country.tsoName || "",
  tsoContactName: country.tsoContactName || "",
  tsoContactEmail: country.tsoContactEmail || "",
  exchangeName: country.exchangeName || "",
  exchangeContactName: country.exchangeContactName || "",
  exchangeContactEmail: country.exchangeContactEmail || "",
  clearingBankName: country.clearingBankName || "",
  clearingBankContactName: country.clearingBankContactName || "",
  clearingBankContactEmail: country.clearingBankContactEmail || "",
  tradingSettlementType: country.tradingSettlementType || "",
  productType: country.productType || "",
  venue: country.venue || "",
  transactionType: country.transactionType || "",
  dealVolume: country.dealVolume,
  unit: country.unit || "",
  mandate: country.mandate ?? false,
  urgent: country.urgent ?? false,
  contractingEntity: country.contractingEntity ?? MFT_ENERGY_AS,
  businessRational: country.businessRational || ""
});

/**
 * Edits the country root, the fields the requester filled in. Everything a department owns lives on
 * its own lifecycle and is edited there.
 *
 * The TSO, exchange and clearing bank here are the **requested** parties, not what Market Access
 * decided. Changing a name here does not move the Market Access name box, and vice versa: they answer
 * different questions and are deliberately separate fields.
 *
 * The VP is not editable here. It carries an access grant, and the backend only accepts a change from
 * Admin or MarketAccess, so it stays where it already is: the Market Access lifecycle.
 */
export const EditCountryDialog: FC<Props> = ({
  open,
  onClose,
  specificCountry
}) => {
  const [formData, setFormData] = useState<CountryFormState>(() =>
    buildFormState(specificCountry)
  );

  const updateCountry = useUpdateCountry({ onSuccess: () => onClose() });

  useEffect(() => {
    if (open) {
      setFormData(buildFormState(specificCountry));
    }
  }, [open, specificCountry]);

  const handleChange = <K extends keyof CountryFormState>(
    key: K,
    value: CountryFormState[K]
  ) => setFormData((current) => ({ ...current, [key]: value }));

  // The same required set the request form enforces. The party fields are all optional there, so
  // they stay optional here rather than trapping an edit behind data the requester never had.
  const submitDisabled =
    !formData.tradingTeam.trim() ||
    !formData.contactPersonTradingTeam.trim() ||
    !formData.countryName.trim() ||
    !formData.tradingSettlementType.trim() ||
    !formData.productType.trim() ||
    !formData.transactionType.trim();

  const handleSave = () => {
    if (submitDisabled) {
      return;
    }

    const trimmed = (value: string) => value.trim() || undefined;

    updateCountry.mutate({
      ...specificCountry,
      tradingTeam: trimmed(formData.tradingTeam),
      contactPersonTradingTeam: trimmed(formData.contactPersonTradingTeam),
      countryName: trimmed(formData.countryName),
      tsoName: trimmed(formData.tsoName),
      tsoContactName: trimmed(formData.tsoContactName),
      tsoContactEmail: trimmed(formData.tsoContactEmail),
      exchangeName: trimmed(formData.exchangeName),
      exchangeContactName: trimmed(formData.exchangeContactName),
      exchangeContactEmail: trimmed(formData.exchangeContactEmail),
      clearingBankName: trimmed(formData.clearingBankName),
      clearingBankContactName: trimmed(formData.clearingBankContactName),
      clearingBankContactEmail: trimmed(formData.clearingBankContactEmail),
      tradingSettlementType: trimmed(formData.tradingSettlementType),
      productType: trimmed(formData.productType),
      venue: trimmed(formData.venue),
      transactionType: trimmed(formData.transactionType),
      dealVolume: formData.dealVolume,
      unit: trimmed(formData.unit),
      mandate: formData.mandate,
      urgent: formData.urgent,
      contractingEntity: formData.contractingEntity.trim() || MFT_ENERGY_AS,
      businessRational: trimmed(formData.businessRational)
    });
  };

  const adornment = (Icon: typeof Groups) => (
    <InputAdornment position="start">
      <Icon fontSize="small" />
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Edit Country Addition</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ paddingTop: 2 }}>
          {updateCountry.isError && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">
                We couldn&apos;t save the country changes. Please try again.
              </Alert>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>Trading team</InputLabel>
                <Select
                  value={formData.tradingTeam}
                  onChange={(e) => handleChange("tradingTeam", e.target.value)}
                  label="Trading team"
                  startAdornment={adornment(Groups)}
                >
                  {mftCompanies.map((company) => (
                    <MenuItem key={company} value={company}>
                      {"MFT " + company.split("MFT")[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <UserNamePicker
                label="Contact person from MFT trading team"
                value={formData.contactPersonTradingTeam}
                onChange={(name) =>
                  handleChange("contactPersonTradingTeam", name)
                }
                required
                startIcon={<Person fontSize="small" />}
              />

              <CountrySelect
                label="Country"
                value={formData.countryName}
                onChange={(name) => handleChange("countryName", name)}
                required
              />

              <TextField
                fullWidth
                label="Contracting entity"
                value={formData.contractingEntity}
                onChange={(e) =>
                  handleChange("contractingEntity", e.target.value)
                }
                slotProps={{
                  input: { startAdornment: adornment(Business) }
                }}
              />

              <TextField
                fullWidth
                required
                label="Type of trading/settlement"
                value={formData.tradingSettlementType}
                onChange={(e) =>
                  handleChange("tradingSettlementType", e.target.value)
                }
                slotProps={{
                  input: { startAdornment: adornment(SwapHoriz) }
                }}
              />

              <TextField
                fullWidth
                required
                label="Product type"
                value={formData.productType}
                onChange={(e) => handleChange("productType", e.target.value)}
                slotProps={{
                  input: { startAdornment: adornment(Category) }
                }}
              />

              <TextField
                fullWidth
                label="Venue"
                value={formData.venue}
                onChange={(e) => handleChange("venue", e.target.value)}
                slotProps={{
                  input: { startAdornment: adornment(Storefront) }
                }}
              />

              <FormControl fullWidth required>
                <InputLabel>Transaction type</InputLabel>
                <Select
                  value={formData.transactionType}
                  onChange={(e) =>
                    handleChange("transactionType", e.target.value)
                  }
                  label="Transaction type"
                  startAdornment={adornment(Repeat)}
                >
                  <MenuItem value="one-off">One-off Transaction</MenuItem>
                  <MenuItem value="recurring">Recurring Transactions</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="TSO (if applicable)"
                value={formData.tsoName}
                onChange={(e) => handleChange("tsoName", e.target.value)}
                slotProps={{
                  input: { startAdornment: adornment(Inventory) }
                }}
              />

              {formData.tsoName.trim() && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="TSO contact name"
                    value={formData.tsoContactName}
                    onChange={(e) =>
                      handleChange("tsoContactName", e.target.value)
                    }
                  />
                  <TextField
                    fullWidth
                    label="TSO contact email"
                    value={formData.tsoContactEmail}
                    onChange={(e) =>
                      handleChange("tsoContactEmail", e.target.value)
                    }
                  />
                </Box>
              )}

              <TextField
                fullWidth
                label="Exchange (if applicable)"
                value={formData.exchangeName}
                onChange={(e) => handleChange("exchangeName", e.target.value)}
                slotProps={{
                  input: { startAdornment: adornment(Storefront) }
                }}
              />

              {formData.exchangeName.trim() && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Exchange contact name"
                    value={formData.exchangeContactName}
                    onChange={(e) =>
                      handleChange("exchangeContactName", e.target.value)
                    }
                  />
                  <TextField
                    fullWidth
                    label="Exchange contact email"
                    value={formData.exchangeContactEmail}
                    onChange={(e) =>
                      handleChange("exchangeContactEmail", e.target.value)
                    }
                  />
                </Box>
              )}

              <TextField
                fullWidth
                label="Clearing bank (if applicable)"
                value={formData.clearingBankName}
                onChange={(e) =>
                  handleChange("clearingBankName", e.target.value)
                }
                slotProps={{
                  input: { startAdornment: adornment(Business) }
                }}
              />

              {formData.clearingBankName.trim() && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Clearing bank contact name"
                    value={formData.clearingBankContactName}
                    onChange={(e) =>
                      handleChange("clearingBankContactName", e.target.value)
                    }
                  />
                  <TextField
                    fullWidth
                    label="Clearing bank contact email"
                    value={formData.clearingBankContactEmail}
                    onChange={(e) =>
                      handleChange("clearingBankContactEmail", e.target.value)
                    }
                  />
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Expected deal volume/size"
                  value={formData.dealVolume ?? ""}
                  onChange={(e) =>
                    handleChange(
                      "dealVolume",
                      e.target.value === "" ? undefined : Number(e.target.value)
                    )
                  }
                  slotProps={{
                    input: { startAdornment: adornment(BarChart) }
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel>Deal Unit</InputLabel>
                  <Select
                    value={formData.unit}
                    onChange={(e) => handleChange("unit", e.target.value)}
                    label="Deal Unit"
                    startAdornment={adornment(Language)}
                  >
                    {UNIT_OPTIONS.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Business Rational"
                value={formData.businessRational}
                onChange={(e) =>
                  handleChange("businessRational", e.target.value)
                }
                slotProps={{
                  input: { startAdornment: adornment(Description) }
                }}
              />

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.mandate}
                      onChange={(e) =>
                        handleChange("mandate", e.target.checked)
                      }
                    />
                  }
                  label="Mandate granted"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.urgent}
                      onChange={(e) => handleChange("urgent", e.target.checked)}
                    />
                  }
                  label="Mark as urgent"
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                paddingTop: 1
              }}
            >
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={submitDisabled || updateCountry.isPending}
              >
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
